import React, { useState, useEffect } from 'react';
import { Navigate } from 'react-router-dom';
import { Building2, Mail, Lock, ArrowRight, AlertCircle } from 'lucide-react';

import { useAuth } from '@context/AuthContext';
import { Input } from '@components/ui/Input';
import { Button } from '@components/ui/Button';
import { useTema } from '@components/M02/casaTema';
import { rutaDeAterrizaje } from '@/utils/navegacion';

const Login: React.FC = () => {
  const T = useTema();
  const { login, isAuthenticated, isLoading, usuario } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  // Si ya está autenticado, redirigir — I-251: la primera pantalla es la lista de Proyectos
  // (Mau, 1-oct: «al entrar al sistema, la primera pantalla es la lista de Proyectos»).
  if (isAuthenticated) {
    return <Navigate to={rutaDeAterrizaje({ nivel: usuario?.nivel ?? 0, modulos: usuario?.modulos ?? [], accesos: usuario?.accesos })} replace state={{ aterrizaje: true }} />;
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!email.trim()) {
      setError('El email es requerido');
      return;
    }
    if (!password) {
      setError('La contraseña es requerida');
      return;
    }

    setSubmitting(true);
    try {
      await login(email.trim(), password);
    } catch (err: unknown) {
      const e = err as Error;
      setError(e.message || 'Error al iniciar sesión. Verifica tus credenciales.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="flex min-h-screen" style={{ backgroundColor: T.panelSoft }}>
      {/* ── Panel izquierdo (ilustración) ─────────────────── */}
      <div
        className="hidden lg:flex flex-col justify-between p-12 flex-shrink-0"
        style={{
          width: '480px',
          background: 'linear-gradient(145deg, #0F172A 0%, #1E3A5F 60%, #1E40AF 100%)',
        }}
      >
        {/* Logo */}
        <div className="flex items-center gap-3">
          <div
            className="flex items-center justify-center rounded-xl"
            style={{ width: '44px', height: '44px', backgroundColor: 'rgba(59,130,246,0.3)', border: '1px solid rgba(59,130,246,0.4)' }}
          >
            <Building2 size={24} color="#60A5FA" />
          </div>
          <div>
            <span className="font-bold text-lg text-white tracking-tight">RM Desarrollos</span>
            <span className="block text-xs" style={{ color: '#64748B' }}>ERP Interno</span>
          </div>
        </div>

        {/* Centro decorativo */}
        <div>
          {/*
            🔴 AQUÍ HABÍA CUATRO NÚMEROS INVENTADOS — «12 proyectos activos», «248 unidades en
            venta», «$4.2M de ingresos del mes» y «37 contratos firmados», escritos a mano.
            El comentario original decía «Stats decorativos», o sea que se sabía; pero **quien
            abre el ERP no lee el comentario, lee los números**, y ésta es la PRIMERA pantalla
            que ve. Cuatro cifras de negocio que el sistema no tiene.

            🔒 Y NO se conectan a datos reales, a propósito: esto es **pre-login**. Enseñar
            aquí los ingresos del mes de la empresa sería publicar sus finanzas a cualquiera
            que abra la dirección, sin contraseña. El arreglo correcto no es traer el dato:
            es **no fingir que hay uno.**

            Quedan los cuatro cuadros —el diseño no se toca— diciendo lo que el ERP hace, que
            sí es cierto y no necesita ninguna cifra.
          */}
          <div className="grid grid-cols-2 gap-3 mb-10">
            {[
              { titulo: 'Ingresos y cobranza', detalle: 'Cobros, cartera y conciliación con el banco' },
              { titulo: 'Egresos y tesorería',  detalle: 'Pagos, autorizaciones y saldo por cuenta' },
              { titulo: 'Obra y presupuesto',   detalle: 'Avances, estimaciones y control de partidas' },
              { titulo: 'Ventas y post-venta',  detalle: 'Prospectos, contratos y garantías' },
            ].map((bloque) => (
              <div
                key={bloque.titulo}
                className="rounded-xl p-4"
                style={{ backgroundColor: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.08)' }}
              >
                <p className="text-sm font-bold text-white mb-1">{bloque.titulo}</p>
                <p className="text-xs leading-relaxed" style={{ color: '#94A3B8' }}>{bloque.detalle}</p>
              </div>
            ))}
          </div>

          <h2 className="text-3xl font-bold text-white mb-3" style={{ letterSpacing: '-0.03em', lineHeight: '1.2' }}>
            Gestión integral de proyectos inmobiliarios
          </h2>
          <p className="text-sm leading-relaxed" style={{ color: '#94A3B8' }}>
            Controla finanzas, obra, ventas y post-venta desde una sola plataforma.
            Diseñada para equipos inmobiliarios en Yucatán.
          </p>
        </div>

        {/* Footer */}
        <p className="text-xs" style={{ color: '#475569' }}>
          © 2026 RM Desarrollos · Mérida, Yucatán
        </p>
      </div>

      {/* ── Panel derecho (formulario) ─────────────────────── */}
      <div className="flex-1 flex items-center justify-center p-8">
        <div className="w-full max-w-sm">
          {/* Logo mobile */}
          <div className="flex items-center gap-2 mb-8 lg:hidden">
            <div
              className="flex items-center justify-center rounded-lg"
              style={{ width: '36px', height: '36px', backgroundColor: T.estados.cfo }}
            >
              <Building2 size={20} color={T.btnFg} />
            </div>
            <span className="font-bold text-lg" style={{ color: T.fg }}>RM ERP</span>
          </div>

          <h1
            className="text-2xl font-bold mb-1"
            style={{ color: T.fg, letterSpacing: '-0.02em' }}
          >
            Bienvenido
          </h1>
          <p className="text-sm mb-8" style={{ color: T.fg3 }}>
            Ingresa tus credenciales para continuar
          </p>

          <form onSubmit={handleSubmit} className="space-y-4" noValidate>
            <Input
              label="Correo electrónico"
              type="email"
              placeholder="tu@rm-desarrollos.com"
              value={email}
              onChange={(e) => { setEmail(e.target.value); setError(''); }}
              icon={<Mail size={15} />}
              required
              autoComplete="email"
              autoFocus
            />
            <Input
              label="Contraseña"
              type="password"
              placeholder="••••••••"
              value={password}
              onChange={(e) => { setPassword(e.target.value); setError(''); }}
              icon={<Lock size={15} />}
              required
              autoComplete="current-password"
            />

            {/* Error */}
            {error && (
              <div
                className="flex items-start gap-2 rounded-lg px-3 py-2.5 text-sm"
                style={{ backgroundColor: T.dangerSoft, border: `1px solid ${T.dangerLine}`, color: T.danger }}
              >
                <AlertCircle size={15} className="flex-shrink-0 mt-0.5" />
                <span>{error}</span>
              </div>
            )}

            <Button
              type="submit"
              variant="primary"
              size="lg"
              fullWidth
              loading={submitting || isLoading}
              icon={<ArrowRight size={16} />}
              iconPosition="right"
            >
              Iniciar sesión
            </Button>
          </form>

          <p className="mt-6 text-xs text-center" style={{ color: T.fg3 }}>
            ¿Problemas para ingresar? Contacta al administrador del sistema.
          </p>
        </div>
      </div>
    </div>
  );
};

export default Login;
